/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useEffect} from 'react';
import {useQuery} from 'react-query';
import {currentUser} from '@ion/core/src/services/Connect';
import {currentUser as sdCurrentUser} from '@ion/core/src/services/SDConnect';
import {User} from '@ion/core/src/types/entities';
import {getQueryClient} from '../config/client';
import {QueryKey} from '../config/QueryKeys';
import {createQueryResult, getStaleTime} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UseCurrentUserResult = UseDefaultQueryResult & {
  currentUser: User
}

/**
 * Provides an imperative API which allows for setting a previously retrieved
 * current user in the query cache.
 *
 * The Hydrate function allows for hydrating the underlying Query Cache with the
 * pre-loaded current User entity. This function is typically implemented in order
 * to support backwards compatibility with existing state managers such that the
 * if the current user has already been retrieved it can be hydrated into the
 * Query cache, and removed from the state managers persistance and accessed
 * exclusively via the Query Cache.
 *
 * ```typescript
 *
 *  import {hydrateCurrentUser} from '@ion/query/src/hooks/useCurrentUser';
 *
 *  try {
 *    const updated = await updateUser(user, context);
 *    if (isCurrentUser) {
 *       hydrateCurrentUser(updated);
 *    }
 *  ...
 *
 * ```
 */
export const hydrateCurrentUser = (user: User) => {
  getQueryClient().setQueryData([QueryKey.CURRENT_USER], user);
};

/**
 * Provides an imperative API which allows for referencing the current User from
 * the query cache, returning the previously Hydrated current User, effectively
 * allowing for an imperative reference (.ts modules) to the current query data
 * for this query.
 *
 * ```typescript
 *
 *  import {dehydrateCurrentUser} from '@ion/query/src/hooks/useCurrentUser';
 *
 *  ...
 *
 *  const currentUser = dehydrateCurrentUser();
 *
 * ```
 */
export const dehydrateCurrentUser = (): User => {
  return getQueryClient().getQueryData([QueryKey.CURRENT_USER]);
};

/**
 * Provides a convenience utility which determines if the specified User is the
 * current User.
 *
 * ```typescript
 *
 *  import {isCurrentUser} from '@ion/query/src/hooks/';
 *
 *  ...
 *
 *  const isCurrent = isCurrentUser(user);
 *
 * ```
 */
export const isCurrentUser = ({id}: User): boolean => {
  return dehydrateCurrentUser()?.id === id;
};

/**
 * The useCurrentUser hook provides the Query for referencing the current user;
 * that is, the authenticated User entity.
 *
 * ```typescript
 *
 * import {useCurrentUser} from '@ion/query/src/hooks/useCurrentUser';
 *
 *  ...
 *
 *  const {currentUser} = useCurrentUser();
 *
 * ```
 *
 * import {useCurrentUser} from '@ion/query/src/hooks/useCurrentUser';
 * @param   enabled when true, the query will be executed immediately. Defaults to
 *          false in order to ensure authentication has been completed prior to
 *          issuing the request.
 * @param   isCTC When true, the CTC current user service will be used (default),
 *          otherwise the SD current user service will be used.
 * @param   staleTime Allows for setting the staleTime for cached query data,
 *          defaults to 60 minutes (CTC session timeout duration).
 * @returns UseCurrentUserResult containing the query results and the current user.
 */
export const useCurrentUser = (enabled = false, isCTC = true, staleTime = getStaleTime(60, false, false)
): UseCurrentUserResult => {
  const query = useQuery([QueryKey.CURRENT_USER], async() => {
    const user = await isCTC ? currentUser() : sdCurrentUser;
    return user;
  }, {staleTime: staleTime as number, enabled});

  useEffect(() => {
    if (enabled && !query.data) {
      query.refetch();
    }
  }, [enabled, query]);

  return createQueryResult(query, {
    currentUser : query.data || {}
  }) as UseCurrentUserResult;
};
export default useCurrentUser;
